/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import difference from 'lodash/difference';
import isUndefined from 'lodash/isUndefined';
import keys from 'lodash/keys';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import type { State } from '../../types.tsx';
import remoteGet from '../remote-get/index.tsx';

type ProjectId = string;

export const get =
	(projectId: ProjectId, properties: string[]): Action<State> =>
	({ getState, setState }) => {
		const currentProperties = getState()[projectId] || {};

		const missingProperties = difference(properties, keys(currentProperties));

		if (!missingProperties.length) {
			return;
		}

		if (fg('project-properties-setter')) {
			// Synchronously mark properties as in-flight BEFORE the async fetch.
			// This prevents concurrent callers (e.g. multiple components mounting simultaneously
			// and each calling get() in their useEffect) from all seeing the same empty state
			// and firing duplicate requests for the same properties.
			// `keys()` returns keys regardless of value, so setting a key to `undefined` here
			// is sufficient to exclude it from `missingProperties` in any concurrent call.
			setState({
				// oxlint-disable-next-line typescript/consistent-type-assertions
				[projectId as string]: {
					...getState()[projectId],
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any
					...missingProperties.reduce<Record<string, any>>((result, name) => {
						const inFlightResult = result;
						inFlightResult[name] = undefined;
						return inFlightResult;
					}, {}),
				},
			});
		}

		remoteGet({
			projectId,
			properties: missingProperties,
		})
			.then((fetchedProperties) => {
				const latestProperties = getState()[projectId] || {};
				setState({
					// oxlint-disable-next-line typescript/consistent-type-assertions
					[projectId as string]: {
						...latestProperties,
						// oxlint-disable-next-line @typescript-eslint/no-explicit-any
						...missingProperties.reduce<Record<string, any>>((result, name) => {
							const reducedResult = result;
							const fetchedProperty = fetchedProperties?.[name];
							// `undefined` inside state means the property is in-flight or was never requested.
							// If value returned for the property is `undefined`, we will set it to `null`,
							// to differentiate it from the in-flight/unrequested state. Otherwise, returned value will be set.
							reducedResult[name] = isUndefined(fetchedProperty) ? null : fetchedProperty;
							return reducedResult;
						}, {}),
					},
				});
			})
			.catch((error) => {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'getProjectProperties',
						packageName: 'jiraProjectPropertiesServices',
						teamName: 'Sequoia',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			});
	};
